<script setup>
// NotePublicView.vue — v5
// Highlight do trecho selecionado e tag pill idênticos ao TextWithNotes.vue

import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/useUserStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import {
  getPublicNoteByToken,
  getPublicTextContent,
} from "@/services/noteService";
import { buildHighlightedExcerpt } from "@/utils/stringUtils";
import { buildHtmlHighlightedExcerpt } from "@/utils/htmlExcerpt";
// Links da nota: extração, previews (imagem, YouTube, documento, site) —
// mesma lógica usada em NoteList.vue.
import { linkifyBody, extractUrls } from "@/utils/noteLinks";
import { useLinkPreviews } from "@/composables/useLinkPreviews";
import api from "@/services/api";

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const noteStore = useNoteStore();
const textStore = useTextStore();
const activityStore = useActivityStore();

// Verificação de links (compartilhada com NoteList.vue): cache de módulo,
// roda fora do carregamento da nota.
const { linkStatus, ensureLinkPreviews } = useLinkPreviews();

const note = ref(null);
const fileContent = ref("");
const isLoading = ref(true);
const notFound = ref(false);

// Trecho inicia colapsado
const isExcerptExpanded = ref(false);

const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

// "Criar conta" — no mobile leva direto para o cadastro mobile (ver LoginForm.vue)
const signupHref = isMobile ? "/login?register=1" : "/login";

onMounted(async () => {
  const token = route.params.shareToken;

  if (!token) {
    notFound.value = true;
    isLoading.value = false;
    return;
  }

  try {
    const response = await getPublicNoteByToken(token);
    note.value = response.data;
    // Registra os links do corpo/fonte para verificação em paralelo (selo de
    // "quebrado" quando o veredito do backend chega). Degrada em silêncio.
    const urls = [
      ...extractUrls(note.value?.body),
      ...extractUrls(note.value?.source),
    ];
    if (urls.length) ensureLinkPreviews(urls);
  } catch {
    notFound.value = true;
    isLoading.value = false;
    return;
  }

  // Usuário autenticado em desktop — redireciona para o app
  // (no mobile o app fica bloqueado, então mantemos a visualização pública)
  if (userStore.isAuthenticated && note.value && !isMobile) {
    await noteStore.resetNoteFiltersToDefault();
    textStore.setIdText(note.value.textId ?? null);
    activityStore.idActivity = note.value.activityId;
    await activityStore.getActivityById(note.value.activityId);
    noteStore.pendingOpenNoteId = note.value.noteId;
    router.push({ name: "RightArea" });
    return;
  }

  // Usuário não autenticado — busca fileContent para exibir o trecho
  if (
    note.value?.textId &&
    note.value.textRangeStart !== note.value.textRangeEnd
  ) {
    try {
      const contentResponse = await getPublicTextContent(note.value.textId);
      const raw = contentResponse.data ?? "";
      // Normaliza exatamente como o textStore faz
      fileContent.value = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    } catch {
      fileContent.value = "";
    }
  }

  isLoading.value = false;
});

// ─────────────────────────────────────────────────────────────────────────────
// Trecho selecionado com contexto de 150 chars
// ─────────────────────────────────────────────────────────────────────────────
const isTextHtml = computed(() => note.value?.textContentFormat === "html");

const selectedTextContent = computed(() => {
  if (!note.value || !fileContent.value) return null;

  if (isTextHtml.value) {
    const html = buildHtmlHighlightedExcerpt(
      fileContent.value,
      note.value.textRangeStart,
      note.value.textRangeEnd,
      150
    );
    return html && { ...html, isHtml: true };
  }

  const plain = buildHighlightedExcerpt(
    fileContent.value,
    note.value.textRangeStart,
    note.value.textRangeEnd,
    150
  );
  return plain && { ...plain, isHtml: false };
});

const hasSelectedText = computed(() => !!selectedTextContent.value);

// ─────────────────────────────────────────────────────────────────────────────
// Estilo do highlight — tag primária define as cores; fallback amarelo se
// não houver tag. Fundo colorido da tag, independente do número de palavras.
// ─────────────────────────────────────────────────────────────────────────────
// Trecho de uma palavra só ganha cor e negrito, sem fundo — mesma convenção
// do NoteCardModal.vue e dos renderizadores (TextWithNotes/TextWithNotesHtml).
// Faltava aqui: o fundo colorido sempre aparecia, palavra única ou não.
const isExcerptSingleWord = computed(() => {
  const content = selectedTextContent.value;
  if (!content) return false;

  const plain = content.isHtml ? content.excerptText : content.excerpt;
  const trimmed = (plain ?? "").trim();
  return trimmed.length > 0 && !/\s/.test(trimmed);
});

const excerptHighlightStyle = computed(() => {
  if (!note.value) return {};

  const tagIds = note.value.tagIds ?? [];
  const tagIsPrimary = note.value.tagIsPrimary ?? [];

  let primaryIdx = tagIsPrimary.findIndex((v) => v === true);
  if (primaryIdx === -1) primaryIdx = 0;

  const tagIdsLength = tagIds.length;

  function pick(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    if (tagIdsLength > 0 && arr.length === tagIdsLength)
      return arr[primaryIdx] ?? null;
    return arr[0] ?? null;
  }

  const textColor = pick(note.value.tagSpecificColors) || "#713f12";
  const bgColor = pick(note.value.tagSpecificBackgrounds) || "#fef08a";

  if (isExcerptSingleWord.value) {
    return {
      backgroundColor: "transparent",
      color: textColor,
      fontWeight: "bold",
    };
  }

  return {
    backgroundColor: bgColor,
    color: textColor,
    fontWeight: "bold",
    borderRadius: "0.5rem",
    padding: "0 4px",
  };
});

// ─────────────────────────────────────────────────────────────────────────────
// Estilo da tag pill
// ─────────────────────────────────────────────────────────────────────────────
function getTagStyle(idx) {
  if (!note.value) return {};
  const textColor = note.value.tagSpecificColors?.[idx] || "#111827";
  const bgColor = note.value.tagSpecificBackgrounds?.[idx] ?? "#f3f4f6";
  return {
    color: textColor,
    backgroundColor: bgColor,
    fontWeight: "bold",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers visuais
// ─────────────────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("pt-BR") +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  if (photoPath.startsWith("http://") || photoPath.startsWith("https://"))
    return photoPath;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = photoPath.startsWith("/") ? photoPath : `/${photoPath}`;
  return `${base}${cleanPath}`;
}

// Corpo da nota com links trocados por previews (imagem, YouTube, documento,
// site) — mesma renderização de NoteList.vue.
function buildNoteBody(note) {
  // Colapsa 3+ quebras de linha consecutivas (mesmo com espaços entre elas)
  // em duas — várias linhas em branco viram uma só. 1 ou 2 quebras são mantidas.
  const raw = String(note?.body ?? "")
    .replace(/[ \t]*\n(?:[ \t]*\n){2,}[ \t]*/g, "\n\n")
    .trim();
  if (!raw) return "—";
  return linkifyBody(raw, { getStatus: (url) => linkStatus[url] });
}

const year = computed(() => new Date().getFullYear());
const hasTags = computed(
  () => Array.isArray(note.value?.tagNames) && note.value.tagNames.length > 0
);
const hasContext = computed(
  () => !!(note.value?.textTitle || note.value?.activityTitle)
);
</script>

<template>
  <div class="public-page">
    <!-- Topbar -->
    <header class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <img
            src="/litterae_pena_fundo_transparente.png"            
            alt="Litterae Logo"
            class="brand-logo"
          />
          <div class="brand-text">
            <div class="brand-name">Litterae</div>
            <div class="brand-tagline">Você não lê sozinho.</div>
          </div>
        </div>
        <nav class="nav-links">
          <a href="/" class="nav-btn nav-btn--ghost">Início</a>
          <a :href="signupHref" class="nav-btn nav-btn--solid"
            >Criar conta</a
          >
        </nav>
      </div>
    </header>

    <main class="public-main">
      <!-- Carregando -->
      <div v-if="isLoading" class="public-state">
        <i class="fas fa-spinner fa-spin public-state-icon"></i>
        <p>Carregando nota…</p>
      </div>

      <!-- Não encontrado -->
      <div v-else-if="notFound" class="public-state public-state-error">
        <div class="info-icon"><i class="fas fa-link-slash"></i></div>
        <h2>Link inválido ou revogado</h2>
        <p>
          Este link de compartilhamento não existe ou foi desativado pelo autor.
        </p>
        <a href="/" class="nav-btn nav-btn--solid state-cta"
          >Ir para o Litterae</a
        >
      </div>

      <!-- Nota -->
      <article v-else-if="note" class="note-card-public">
        <!-- Autor -->
        <div class="note-author-row">
          <div class="chip-avatar">
            <div
              v-if="getAvatarUrl(note.createdByUserPhotoPath)"
              class="chip-avatar-img"
              :style="{
                backgroundImage: `url('${getAvatarUrl(
                  note.createdByUserPhotoPath
                )}')`,
              }"
            ></div>
            <i
              v-else
              class="fas fa-user chip-avatar-icon"
              aria-hidden="true"
            ></i>
          </div>
          <div class="note-author-meta">
            <span class="note-author-name">{{ note.createdByUserName }}</span>
            <span v-if="formatDate(note.updatedAt)" class="note-author-date">
              {{ formatDate(note.updatedAt) }}
            </span>
          </div>
        </div>

        <!-- Trecho referenciado pela nota (colapsado por padrão) -->
        <div v-if="hasSelectedText" class="selected-text-section">
          <label class="note-section-label"
            >Trecho referenciado pela nota</label
          >
          <div class="excerpt-wrapper">
            <blockquote
              class="selected-text-blockquote"
              :class="{ 'is-collapsed': !isExcerptExpanded }"
            >
              <span
                v-if="selectedTextContent.truncatedBefore"
                class="excerpt-ellipsis"
                >…</span
              >
              <span
                v-if="selectedTextContent.isHtml"
                class="excerpt-before"
                v-html="selectedTextContent.before"
              ></span>
              <span v-else class="excerpt-before">{{
                selectedTextContent.before
              }}</span>

              <mark
                v-if="selectedTextContent.isHtml"
                class="excerpt-highlight"
                :style="excerptHighlightStyle"
                v-html="selectedTextContent.excerpt"
              ></mark>
              <mark
                v-else
                class="excerpt-highlight"
                :style="excerptHighlightStyle"
                >{{ selectedTextContent.excerpt }}</mark
              >

              <span
                v-if="selectedTextContent.isHtml"
                class="excerpt-after"
                v-html="selectedTextContent.after"
              ></span>
              <span v-else class="excerpt-after">{{
                selectedTextContent.after
              }}</span>
              <span
                v-if="selectedTextContent.truncatedAfter"
                class="excerpt-ellipsis"
                >…</span
              >
            </blockquote>

            <button
              type="button"
              class="excerpt-toggle-btn"
              @click="isExcerptExpanded = !isExcerptExpanded"
              :aria-label="
                isExcerptExpanded ? 'Colapsar trecho' : 'Expandir trecho'
              "
            >
              <i
                :class="
 isExcerptExpanded
                    ? 'fas fa-chevron-up'
                    : 'fas fa-chevron-down'
                "
              ></i>
            </button>
          </div>
        </div>

        <!-- Título -->
        <h1 class="note-title-public">{{ note.title || "Sem Título" }}</h1>

        <!-- Conteúdo da nota (links viram previews de imagem/vídeo/site/documento) -->
        <blockquote class="note-body-public" v-html="buildNoteBody(note)"></blockquote>

        <!-- Tags -->
        <div v-if="hasTags" class="note-tags-row">
          <i class="fas fa-hashtag note-tag-hash"></i>
          <span
            v-for="(tagName, idx) in note.tagNames"
            :key="`tag-${note.tagIds?.[idx] ?? idx}`"
            class="note-tag-pill"
            :style="getTagStyle(idx)"
            >{{ tagName }}</span
          >
        </div>

        <!-- Fonte -->
        <div v-if="note.source" class="note-source-row">
          <i class="fas fa-link note-source-icon"></i>
          <span class="note-source-text">{{ note.source }}</span>
        </div>

        <!-- Contexto -->
        <div v-if="hasContext" class="note-context-row">
          <template v-if="note.textTitle">
            <span class="note-context-label">Texto</span>
            <span class="note-context-chip note-context-chip-text">
              <i class="fas fa-book-open note-context-icon"></i>
              {{ note.textTitle }}
            </span>
            <span v-if="note.textSubtitle" class="note-context-subtitle-line">
              {{ note.textSubtitle }}
            </span>
          </template>
          <template v-if="note.activityTitle">
            <span class="note-context-label" style="margin-top: 6px"
              >Atividade</span
            >
            <span class="note-context-chip note-context-chip-text">
              <i class="fas fa-tasks note-context-icon"></i>
              {{ note.activityTitle }}
            </span>
          </template>
        </div>

        <!-- Rodapé -->
        <footer class="note-public-footer">
          <span>Criado no</span>
          <a href="/" class="note-footer-link">Litterae</a>
        </footer>
      </article>
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <img
            src="/litterae_pena_fundo_transparente.png"
            alt="Litterae"
            class="footer-logo"
          />
          <div>
            <div class="footer-name">Litterae</div>
            <div class="footer-muted">© {{ year }} — Você não lê sozinho.</div>
          </div>
        </div>
        <div class="footer-links">
          <a :href="signupHref">Criar Conta</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css");

.public-page {
  min-height: 100vh;
  background: var(--bg-main);
  color: var(--text-dark);
  font-family: var(--font-family);
  display: flex;
  flex-direction: column;
}

/* ── Topbar ── */
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--bg-sidebar-left);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}
.topbar-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  border-radius: 12px;
}
.brand-text {
  display: flex;
  flex-direction: column;
}
.brand-name {
  font-weight: 800;
  font-size: 1.1rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
}
.brand-tagline {
  font-size: 0.85rem;
  color: var(--text-light);
  line-height: 1.2;
}
.nav-links {
  display: flex;
  align-items: center;
  gap: 10px;
}
.nav-btn {
  text-decoration: none;
  font-weight: 700;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
  font-size: 0.95rem;
  white-space: nowrap;
  cursor: pointer;
}
.nav-btn--ghost {
  background: rgba(255, 255, 255, 0.65);
  color: var(--text-dark);
}
.nav-btn--solid {
  background: var(--brand-light);
  color: var(--text-on-brand);
  border-color: transparent;
}
.nav-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
}

/* ── Main ── */
.public-main {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2.5rem 1rem 4rem;
}

/* ── Estados ── */
.public-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 5rem 2rem;
  color: var(--text-light);
  text-align: center;
  max-width: 420px;
  width: 100%;
}
.public-state-icon {
  font-size: 2rem;
  opacity: 0.45;
}
.public-state-error h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}
.public-state-error p {
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.55;
}
.info-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(160, 196, 255, 0.35);
  border: 1px solid rgba(160, 196, 255, 0.55);
  font-size: 1.2rem;
  color: var(--text-dark);
}
.state-cta {
  margin-top: 6px;
  display: inline-block;
}

/* ── Card da nota ── */
.note-card-public {
  background: var(--bg-content);
  border: 1px solid var(--border-color);
  border-radius: 22px;
  padding: 1.75rem;
  width: 100%;
  max-width: 700px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Autor ── */
.note-author-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.chip-avatar {
  width: 36px;
}

.note-author-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.note-author-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
}
.note-author-date {
  font-size: 0.72rem;
  color: var(--text-light);
  font-style: italic;
}

/* ── Título ── */
.note-title-public {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-dark);
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-align: justify;
  hyphens: auto;
  overflow-wrap: anywhere;
}

/* ── Label de seção ── */
.note-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Trecho selecionado ── */
.selected-text-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.excerpt-wrapper {
  position: relative;
}

.selected-text-blockquote {
  white-space: pre-wrap;
  line-height: 1.55;
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid var(--brand-light);
  background-color: var(--bg-header);
  color: var(--text-dark);
  font-size: 0.9rem;
  /* SEM font-family: monospace — evita distorção no underline */
  font-family: var(--font-family);
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.selected-text-blockquote.is-collapsed {
  max-height: 60px;
  overflow: hidden;
  mask-image: linear-gradient(to bottom, black 30%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 30%, transparent 100%);
}

/* Botão colapsar/expandir */
.excerpt-toggle-btn {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--bg-content);
  color: var(--text-light);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.72rem;
  z-index: 2;
  transition: background-color 0.15s, transform 0.15s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.excerpt-toggle-btn:hover {
  background: var(--neutral-100);
  transform: translateX(-50%) translateY(50%) scale(1.1);
}

/* Contexto antes/depois do trecho */
.excerpt-before,
.excerpt-after {
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-light);
  font-size: 0.88rem;
}

/* Highlight do trecho — cores via :style inline (excerptHighlightStyle) */
.excerpt-highlight {
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0 2px;
  transition: background-color 0.15s, color 0.15s;
}

.excerpt-ellipsis {
  color: var(--text-light);
  font-style: italic;
  font-size: 0.85rem;
  user-select: none;
}

/* ── Conteúdo da nota ── */
.note-body-public {
  white-space: pre-wrap;
  line-height: 1.6;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid var(--border-color);
  border-left: 4px solid var(--brand-light);
  color: var(--text-dark);
  font-size: 0.94rem;
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
}

/* ── Previews de links no corpo (imagem / vídeo / documento / site) ──
   Espelhados de NoteList.vue (.note-content) para manter a mesma aparência. */
.note-body-public :deep(.note-media-row) {
  /* inline-flex (não flex/block) para não gerar linha em branco extra somada
     ao \n preservado pelo white-space: pre-wrap — mantém o lado-a-lado + wrap. */
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  vertical-align: top;
  margin: 0;
}
.note-body-public :deep(.note-media-row .note-preview) {
  margin: 0;
}
.note-body-public :deep(.note-preview) {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 96px;
  vertical-align: top;
  margin: 2px 6px 2px 0;
  text-decoration: none;
  line-height: 1.2;
}
.note-body-public :deep(.note-preview-thumb) {
  position: relative;
  width: 96px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--surface-2);
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.1);
  overflow: hidden;
  transition: transform 0.12s ease, box-shadow 0.15s ease;
}
.note-body-public :deep(.note-preview:hover .note-preview-thumb) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.16);
}
.note-body-public :deep(.note-preview-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.note-body-public :deep(.note-preview-icon) {
  font-size: 2.1rem;
  line-height: 1;
}
.note-body-public :deep(.note-preview-favicon) {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
.note-body-public :deep(.note-preview-fallback) {
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--text-subtle);
}
.note-body-public :deep(.note-preview-caption) {
  max-width: 96px;
  font-size: 0.72rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.note-body-public :deep(.note-preview-play) {
  position: absolute;
  bottom: 4px;
  right: 5px;
  font-size: 1.05rem;
  line-height: 1;
  color: #ff0000;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}
.note-body-public :deep(.note-preview-thumb--broken),
.note-body-public :deep(.note-preview-fallback--broken) {
  position: relative;
  border-color: var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-hover);
}
.note-body-public :deep(.note-preview-thumb--broken .note-preview-icon) {
  color: var(--danger-hover);
  opacity: 0.55;
}
.note-body-public :deep(.note-preview-badge) {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--danger-hover);
  color: #fff;
  font-size: 0.55rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.note-body-public :deep(.note-preview-caption--broken),
.note-body-public :deep(.note-preview--broken .note-preview-caption) {
  color: var(--danger-text);
  text-decoration: line-through;
}
.note-body-public :deep(.note-preview--broken .note-preview-play) {
  display: none;
}

/* ── Tags ── */
.note-tags-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.note-tag-hash {
  font-size: 0.72rem;
  color: var(--text-light);
  opacity: 0.8;
}

/* pill da tag — border-radius e padding controlados aqui;
   cores via :style inline (getTagStyle) */
.note-tag-pill {
  display: inline;
  padding: 1px 4px;
  border-radius: 0.5rem; /* idêntico ao rounded-lg do Tailwind = 8px */
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 3px;
}

/* ── Fonte ── */
.note-source-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-light);
  font-style: italic;
}
.note-source-icon {
  font-size: 0.65rem;
  margin-top: 3px;
  flex-shrink: 0;
}
.note-source-text {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* ── Contexto (Texto / Atividade) ── */
.note-context-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px dashed var(--border-color);
}
.note-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.note-context-chip {
  font-size: 0.84rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--text-light);
}
.note-context-icon {
  font-size: 0.68rem;
  opacity: 0.8;
}
.note-context-subtitle-line {
  font-size: 0.82rem;
  font-style: italic;
  color: var(--text-light);
  margin-left: 18px;
  margin-top: -2px;
}

/* ── Rodapé da nota ── */
.note-public-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);
  font-size: 0.78rem;
  color: var(--text-light);
  flex-wrap: wrap;
}
.note-footer-link {
  color: var(--text-on-brand-soft);
  font-weight: 700;
  text-decoration: none;
}
.note-footer-link:hover {
  text-decoration: underline;
}

/* ── Footer da página ── */
.footer {
  padding: 22px 0 34px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-main);
}
.footer-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}
.footer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.footer-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
.footer-name {
  font-weight: 900;
  color: var(--text-dark);
}
.footer-muted {
  color: var(--text-light);
  font-size: 0.9rem;
}
.footer-links {
  display: flex;
  align-items: center;
  gap: 12px;
}
.footer-links a {
  color: var(--text-light);
  text-decoration: none;
  font-weight: 700;
}
.footer-links a:hover {
  text-decoration: underline;
}
</style>
